/*
  NISS-0138 Provider Intake Classifier — Questionnaire definition.

  Phase 4: SIL Registration Group 0138 pathway classifier fields.
  The 22 spec-required intake fields have been added across new and existing
  sections without duplicating semantic equivalents.

  All questions deliberately avoid asking for participant identifiers,
  detailed disability information, identifying incident details, or
  unnecessary worker private information. Provider name + ABN are public
  business attributes and ARE collected.
*/

// Evidence answer scale shared by every Evidence Mapper item.
// Values are stable (stored in localStorage); labels follow Design
// Specification 001 — Applicability Assessment (Phase 1).
export const EVIDENCE_ANSWERS = [
  { value: "current", short: "Current", label: "Current and available" },
  { value: "review", short: "Review", label: "Exists but needs review" },
  { value: "missing", short: "Missing", label: "Missing" },
  { value: "unsure", short: "Needs clarification", label: "Needs Clarification \u2014 cannot yet confidently determine whether participant outcomes are adequately protected" },
  { value: "na", short: "Not applicable", label: "Not applicable \u2014 explanation required" }
];

// Evidence condition values available once an item is assessed as Applicable.
export const EVIDENCE_CONDITION_VALUES = ["current", "review", "missing"];

// Design Specification 001 — permitted applicability statuses only.
export const APPLICABILITY_OPTIONS = [
  { value: "applicable", short: "Applicable", description: "Item is relevant to the provider, participant context, support model, service setting, or readiness review." },
  { value: "na", short: "Not applicable", description: "Item is not relevant to this readiness review. A brief explanation is required." },
  { value: "unsure", short: "Needs clarification", description: "Cannot yet confidently determine whether participant outcomes are adequately protected \u2014 more information, evidence, or context is required." }
];

// P2 — Per-field source verification (display/transparency only).
// These states never feed scoring, classification, or the report-level
// source verification status, and none of them is ever forced.
export const FIELD_VERIFICATION_LABELS = {
  source_verified: "Source verified",
  source_required: "Source required",
  provider_reported: "Provider-reported",
  consultant_observed: "Consultant-observed",
  not_applicable: "Not applicable, with explanation",
  requires_clarification: "Requires clarification"
};

// Consultant-selectable states for Applicable items. Provider-reported is the
// conservative default \u2014 intake responses are provider self-reported until
// the consultant records otherwise.
export const FIELD_VERIFICATION_OPTIONS = [
  { value: "provider_reported", short: "Provider-reported", description: "Reported by the provider during intake and not yet independently sighted." },
  { value: "consultant_observed", short: "Consultant-observed", description: "The consultant has directly sighted or observed the underlying material." },
  { value: "source_required", short: "Source required", description: "This item may require supporting evidence before it can be relied on." },
  { value: "source_verified", short: "Source verified", description: "A supporting source has been identified and checked by the consultant for this item." }
];

// ---------------------------------------------------------------------------
// Phase 8C — Canonical evidence state (6 states, display-only, derived on read).
//
// The stored data model is unchanged. Legacy answer tokens
// (`current`/`review`/`missing`/`unsure`/`na`) and legacy verification tokens
// (`provider_reported` / `consultant_observed` / `source_required` /
// `source_verified`) are preserved verbatim in `answers`. `getCanonicalEvidenceState`
// deterministically maps every legacy combination onto one of the six
// canonical display states below.
//
// Source verified is provenance/verification only. It does not imply legal
// compliance, audit approval or certification, and readiness scoring never
// uses this label.
// ---------------------------------------------------------------------------
export const CANONICAL_EVIDENCE_STATE = {
  PROVIDER_REPORTED: "Provider-reported",
  CONSULTANT_OBSERVED: "Consultant-observed",
  SOURCE_REQUIRED: "Source required",
  SOURCE_VERIFIED: "Source verified",
  REQUIRES_CLARIFICATION: "Requires clarification",
  NOT_APPLICABLE: "Not applicable, with explanation"
};

export const CANONICAL_EVIDENCE_STATES = Object.values(CANONICAL_EVIDENCE_STATE);

// Derive the canonical state for a single evidence item.
//   condition       — one of "current" | "review" | "missing" | "unsure" | "na"
//                     (legacy stored token on answers[item.id])
//   verification    — one of the FIELD_VERIFICATION_OPTIONS values, or falsy
//                     (legacy stored token on answers[item.id + "__verification"])
//   naReason        — trimmed explanation string when condition === "na"
//
// Returns one of CANONICAL_EVIDENCE_STATE values, or null when the item has
// not yet been answered at all.
export function getCanonicalEvidenceState(condition, verification, naReason) {
  if (!condition) return null;
  if (condition === "na") {
    // Not applicable is valid only with an explanation; otherwise clarify.
    if (naReason && String(naReason).trim()) {
      return CANONICAL_EVIDENCE_STATE.NOT_APPLICABLE;
    }
    return CANONICAL_EVIDENCE_STATE.REQUIRES_CLARIFICATION;
  }
  if (condition === "unsure") {
    return CANONICAL_EVIDENCE_STATE.REQUIRES_CLARIFICATION;
  }
  if (condition === "missing") {
    // Evidence is missing → the item needs a source to be provided.
    return CANONICAL_EVIDENCE_STATE.SOURCE_REQUIRED;
  }
  if (condition === "review") {
    // "Exists but needs review" maps to Requires clarification unless the
    // consultant has already recorded a stronger verification state.
    if (verification === "source_verified") {
      return CANONICAL_EVIDENCE_STATE.SOURCE_VERIFIED;
    }
    if (verification === "consultant_observed") {
      return CANONICAL_EVIDENCE_STATE.CONSULTANT_OBSERVED;
    }
    return CANONICAL_EVIDENCE_STATE.REQUIRES_CLARIFICATION;
  }
  // condition === "current" → applicable path — use verification.
  // Intake "Current and available" without verification remains
  // Provider-reported (provider self-report metadata), never Source verified.
  switch (verification) {
    case "source_verified":
      return CANONICAL_EVIDENCE_STATE.SOURCE_VERIFIED;
    case "consultant_observed":
      return CANONICAL_EVIDENCE_STATE.CONSULTANT_OBSERVED;
    case "source_required":
      return CANONICAL_EVIDENCE_STATE.SOURCE_REQUIRED;
    case "provider_reported":
    default:
      // Conservative default when verification is missing — provider self-report.
      return CANONICAL_EVIDENCE_STATE.PROVIDER_REPORTED;
  }
}

// True when the recorded evidence has enough information to count as complete.
// Not applicable requires an explanation; every other canonical state is
// self-complete.
export function isEvidenceCanonicalComplete(condition, verification, naReason) {
  const state = getCanonicalEvidenceState(condition, verification, naReason);
  if (!state) return false;
  // Not applicable answers are complete only when an explanation exists.
  // Without explanation, display maps to Requires clarification but remains incomplete.
  if (condition === "na") {
    return Boolean(naReason && String(naReason).trim());
  }
  if (state === CANONICAL_EVIDENCE_STATE.NOT_APPLICABLE) {
    return Boolean(naReason && String(naReason).trim());
  }
  return true;
}

// 17 evidence categories mapped to the R.E.A.D.Y. framework.
export const EVIDENCE_ITEMS = [
  { id: "ev_registration_profile", number: 1, title: "Registration and provider profile", helper: "Provider details, registration class and scope match the services actually delivered.", domain: "R" },
  { id: "ev_governance", number: 2, title: "Governance and responsibility", helper: "Documented accountable persons, board or leadership oversight, decision-making record.", domain: "E" },
  { id: "ev_policies_procedures", number: 3, title: "Policies and procedures", helper: "Core operational policies are current, version-controlled and accessible to staff.", domain: "E" },
  { id: "ev_incident_management", number: 4, title: "Incident management", helper: "Documented procedure for recording, escalating and reviewing incidents (non-identifying).", domain: "D" },
  { id: "ev_complaints_management", number: 5, title: "Complaints management", helper: "Documented complaints procedure, recording register, and resolution loop.", domain: "E" },
  { id: "ev_risk_management", number: 6, title: "Risk management", helper: "Risk register, control owners, periodic review cycle.", domain: "A" },
  { id: "ev_worker_screening", number: 7, title: "Worker screening", helper: "Worker screening checks are current and tracked centrally.", domain: "A" },
  { id: "ev_staff_induction", number: 8, title: "Staff induction", helper: "Documented induction programme covering safeguarding, incidents and code of conduct.", domain: "A" },
  { id: "ev_staff_training", number: 9, title: "Staff training", helper: "Training matrix with currency dates against role requirements.", domain: "A" },
  { id: "ev_medication_process", number: 10, title: "Medication support process", helper: "Documented medication support policy, competency, and recording procedure.", domain: "D" },
  { id: "ev_handover", number: 11, title: "Shift handover process", helper: "Structured handover process with documented expectations for what must be transferred.", domain: "A" },
  { id: "ev_rostering_supervision", number: 12, title: "Rostering and supervision", helper: "Rosters and supervision arrangements are documented, reviewed and aligned to participant need.", domain: "A" },
  { id: "ev_transition_process", number: 13, title: "Participant transition process", helper: "Documented process for safe transitions between supports or providers.", domain: "Y" },
  { id: "ev_service_agreements", number: 14, title: "Service agreements", helper: "Current service agreements aligned to scope of registration and pricing arrangements.", domain: "E" },
  { id: "ev_quality_improvement", number: 15, title: "Quality improvement records", helper: "Continuous improvement register with actions, owners and review dates.", domain: "E" },
  { id: "ev_safeguarding_escalation", number: 16, title: "Safeguarding escalation process", helper: "Documented escalation pathway for abuse, neglect, restrictive practice and serious incidents.", domain: "D" },
  { id: "ev_exit_transition_planning", number: 17, title: "Exit or transition planning", helper: "Documented plan for organisational wind-down or scope change that protects participant continuity.", domain: "Y" }
];

export const READY_DOMAINS = [
  { key: "R", title: "Registration Pathway" },
  { key: "E", title: "Evidence Mapping" },
  { key: "A", title: "Operational Risk" },
  { key: "D", title: "Danger \u00b7 Safeguarding" },
  { key: "Y", title: "Your 30 / 60 / 90 Plan" }
];

const YES_NO_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" }
];

const YES_NO_UNSURE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Unsure" }
];

const YES_NO_UNSURE_NA_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Unsure" },
  { value: "na", label: "Not applicable" }
];

// QC-03 — SIL provider-managed workforce may be a shared arrangement or unclear.
// Shared and unsure are clarification signals (not scored).
const YES_NO_SHARED_UNSURE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "shared", label: "Shared" },
  { value: "unsure", label: "Unsure" }
];

// QC-04, QC-05 — readiness/evidence signals only. Missing means "Not yet assessed".
const YES_PARTLY_NO_UNSURE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "partly", label: "Partly" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Unsure" }
];

// QC-08 — readiness/referral signal only. NA is preserved for arrangements the
// provider does not control (e.g. participant-managed tenancy).
const YES_PARTLY_NO_UNSURE_NA_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "partly", label: "Partly" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Unsure" },
  { value: "na", label: "Not applicable" }
];

const SAFEGUARDING_OPTIONS = [
  { value: "no", label: "No \u2014 not at this time" },
  { value: "yes", label: "Yes \u2014 a concern exists" },
  { value: "undisclosed", label: "Prefer not to answer" }
];

const APP_STATUS_OPTIONS = [
  { value: "not_started", label: "Not started" },
  { value: "drafting", label: "Drafting application" },
  { value: "lodged", label: "Lodged \u2014 awaiting assessment" },
  { value: "under_assessment", label: "Under NDIS Commission assessment" },
  { value: "audit_scheduled", label: "Audit scheduled or in progress" },
  { value: "decision_received", label: "Decision received" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "not_planning", label: "Not planning to apply" }
];

export const SECTIONS = [
  {
    id: "profile",
    letter: "\u00a7",
    code: "01",
    title: "Provider Profile",
    subtitle:
      "Public organisation details and operating context. Do not enter participant or worker information.",
    questions: [
      {
        id: "provider_name",
        prompt: "Provider organisation name",
        helper: "Trading name or legal entity name of the provider organisation. Do not enter participant or worker identifiers.",
        type: "shorttext",
        placeholder: "e.g. Example Care Services Pty Ltd"
      },
      {
        id: "provider_abn",
        prompt: "ABN",
        helper: "11-digit Australian Business Number. Numbers only \u2014 spaces are allowed during entry.",
        type: "abn",
        placeholder: "e.g. 12 345 678 901"
      },
      {
        id: "profile_registration_status",
        prompt: "Which statement best describes your current NDIS registration position?",
        type: "single",
        options: [
          { value: "registered", label: "Currently registered NDIS provider" },
          { value: "unregistered_considering", label: "Unregistered, considering registration" },
          { value: "unregistered_not_planning", label: "Unregistered, not planning to register" },
          { value: "in_process", label: "In an active registration or audit process" },
          { value: "unsure", label: "Unclear or unsure" }
        ]
      },
      {
        id: "provider_0115_held",
        prompt: "Does the organisation currently hold registration group 0115?",
        type: "single",
        options: YES_NO_OPTIONS
      },
      {
        id: "provider_registration_groups",
        prompt: "Current registration groups (optional)",
        helper: "Comma-separated list of NDIS registration groups currently held \u2014 e.g. 0115, 0107, 0125.",
        type: "longtext",
        optional: true,
        placeholder: "e.g. 0107, 0115, 0125"
      },
      {
        id: "profile_service_focus",
        prompt: "Which service description best matches your primary delivery model?",
        type: "single",
        options: [
          { value: "sil_0115", label: "SIL \u2014 Support Independent Living (0115) only" },
          { value: "sil_0138", label: "Specialised supports under 0138 only" },
          { value: "mixed", label: "A mix of 0115 and 0138 supports" },
          { value: "other", label: "Other community supports only" },
          { value: "unsure", label: "Unclear or unsure" }
        ]
      },
      {
        id: "profile_participant_scale",
        prompt: "Approximately how many participants does your organisation support?",
        type: "single",
        options: [
          { value: "1_5", label: "1 \u2013 5" },
          { value: "6_20", label: "6 \u2013 20" },
          { value: "21_50", label: "21 \u2013 50" },
          { value: "51_plus", label: "51 +" },
          { value: "undisclosed", label: "Prefer not to indicate" }
        ]
      },
      {
        id: "profile_operating_years",
        prompt: "How long has the organisation been operating in its current service model?",
        type: "single",
        options: [
          { value: "lt_1", label: "Less than 12 months" },
          { value: "1_3", label: "1 \u2013 3 years" },
          { value: "4_7", label: "4 \u2013 7 years" },
          { value: "8_plus", label: "8 + years" }
        ]
      }
    ]
  },
  {
    id: "registration",
    letter: "R",
    code: "02",
    title: "Registration Pathway",
    subtitle:
      "The R in R.E.A.D.Y. \u2014 confirming the registration class and scope that applies today.",
    questions: [
      {
        id: "registration_pathway_clarity",
        prompt: "Is the registration pathway that applies to your services clear to you?",
        type: "single",
        options: [
          { value: "clear", label: "Yes \u2014 clear and documented" },
          { value: "mostly", label: "Mostly \u2014 minor uncertainty" },
          { value: "partial", label: "Partially \u2014 significant uncertainty" },
          { value: "unclear", label: "No \u2014 unclear pathway" },
          { value: "undisclosed", label: "Prefer not to answer" }
        ]
      },
      {
        id: "registration_audit_status",
        prompt: "What best describes the current state of your NDIS audit certification?",
        type: "single",
        options: [
          { value: "current_in_scope", label: "Current and within scope" },
          { value: "current_out_scope", label: "Current, but scope may not match services" },
          { value: "lapsed", label: "Expired or lapsed" },
          { value: "never_held", label: "Never held" },
          { value: "unsure", label: "Unsure" }
        ]
      },
      {
        id: "registration_scope_accuracy",
        prompt: "Does your registration scope align to the services actually delivered today?",
        type: "single",
        options: [
          { value: "fully", label: "Fully aligned" },
          { value: "mostly", label: "Mostly aligned with minor gaps" },
          { value: "partial", label: "Partially aligned" },
          { value: "misaligned", label: "Not aligned / significant misalignment" },
          { value: "na", label: "Not applicable \u2014 unregistered" }
        ]
      }
    ]
  },
  {
    id: "sil_delivery",
    letter: "R",
    code: "03",
    title: "SIL Service Delivery",
    subtitle:
      "Eligibility criteria used to estimate 0138 exposure likelihood. Answer Yes only if the criterion clearly applies to the way you operate today.",
    questions: [
      { id: "sil_currently_delivering", prompt: "Currently delivering SIL?", type: "single", options: YES_NO_OPTIONS },
      { id: "sil_before_july_2026", prompt: "Was the organisation delivering SIL before 1 July 2026?", type: "single", options: YES_NO_OPTIONS },
      { id: "sil_all_or_most_day", prompt: "Is the support required all or most of the day?", type: "single", options: YES_NO_UNSURE_OPTIONS },
      { id: "sil_provider_manages", prompt: "Does the provider manage, coordinate, or roster the support workers?", type: "single", options: YES_NO_SHARED_UNSURE_OPTIONS },
      { id: "sil_participant_self_manages", prompt: "Does the participant choose and manage their own workers?", type: "single", options: YES_NO_OPTIONS },
      { id: "sil_shared_living", prompt: "Is the support delivered in a shared living or group home setting?", type: "single", options: YES_NO_OPTIONS },
      { id: "sil_provider_controls_tenancy", prompt: "Does the provider control the tenancy or housing arrangement?", type: "single", options: YES_NO_OPTIONS },
      {
        id: "sil_package_daily_life_purpose",
        prompt: "Does the SIL package support the participant\u2019s daily life and purpose?",
        helper: "Readiness qualifier. Provider-reported observation only \u2014 evidence verification stays downstream. Leave unanswered if not yet assessed.",
        type: "single",
        optional: true,
        options: YES_PARTLY_NO_UNSURE_OPTIONS
      },
      {
        id: "practice_supported_decision_making",
        prompt: "Does the service actively practise supported decision-making with participants?",
        helper: "Readiness / evidence signal only. Not scored as operational risk. Evidence verification stays downstream. Leave unanswered if not yet assessed.",
        type: "single",
        optional: true,
        options: YES_PARTLY_NO_UNSURE_OPTIONS
      }
    ]
  },
  {
    id: "application",
    letter: "R",
    code: "04",
    title: "Application Status",
    subtitle:
      "Current state of any active or planned NDIS registration application. Date and number are optional if no application has been lodged. Existing unregistered SIL providers who were delivering SIL before 1 July 2026 must be assessed against the 1 October 2026 application deadline.",
    questions: [
      { id: "app_status", prompt: "Current application status", type: "single", options: APP_STATUS_OPTIONS },
      { id: "app_date", prompt: "Application date", helper: "Date the application was lodged or drafted. Leave blank if none.", type: "date", optional: true },
      { id: "app_number", prompt: "Application number", helper: "NDIS application or reference number, if available.", type: "shorttext", optional: true, placeholder: "e.g. APP-2026-0001" },
      { id: "app_aqa_engaged", prompt: "Has an Approved Quality Auditor (AQA) been engaged?", type: "single", options: YES_NO_OPTIONS },
      { id: "app_audit_scope_received", prompt: "Has the audit scope been received?", type: "single", options: YES_NO_OPTIONS },
      { id: "app_plan_continue", prompt: "Plan to continue delivering SIL?", type: "single", options: YES_NO_UNSURE_OPTIONS },
      { id: "app_plan_exit", prompt: "Plan to exit SIL delivery?", type: "single", options: YES_NO_UNSURE_OPTIONS }
    ]
  },
  {
    id: "evidence",
    letter: "E",
    code: "05",
    title: "Evidence Mapping",
    subtitle:
      "The E in R.E.A.D.Y. \u2014 a 17-item evidence map grouped by framework domain. Mark each item against the operating reality, not the intent.",
    customComponent: "EvidenceMapper",
    items: EVIDENCE_ITEMS,
    questions: EVIDENCE_ITEMS.map((item) => ({ id: item.id, optional: false })),
    sharedOptions: EVIDENCE_ANSWERS
  },
  {
    id: "risk",
    letter: "A",
    code: "06",
    title: "Assessment of Operational Risk",
    subtitle:
      "The A in R.E.A.D.Y. \u2014 the gap between policy and day\u2011to\u2011day delivery.",
    questions: [
      {
        id: "risk_staffing_supervision",
        prompt: "How would you describe your current staffing and supervision arrangements?",
        type: "single",
        options: [
          { value: "strong", label: "Fully staffed with documented supervision" },
          { value: "stable_informal", label: "Stable staffing, supervision is informal" },
          { value: "pressure_recent", label: "Recent or recurring staffing pressure" },
          { value: "pressure_significant", label: "Significant staffing or supervision pressure" },
          { value: "unsure", label: "Unsure" }
        ]
      },
      {
        id: "risk_continuity_concerns",
        prompt: "Are there current concerns about service continuity?",
        type: "single",
        options: [
          { value: "none", label: "No concerns" },
          { value: "minor_monitored", label: "Minor concerns being monitored" },
          { value: "active", label: "Active concerns under management" },
          { value: "significant", label: "Significant or unresolved concerns" },
          { value: "unsure", label: "Unsure" }
        ]
      },
      {
        id: "risk_practice_gap",
        prompt: "How aligned are your documented procedures with day\u2011to\u2011day practice?",
        type: "single",
        options: [
          { value: "close", label: "Closely aligned" },
          { value: "minor", label: "Minor variation" },
          { value: "notable", label: "Notable variation" },
          { value: "significant", label: "Significant gap" },
          { value: "unsure", label: "Unsure" }
        ]
      }
    ]
  },
  {
    id: "safeguarding",
    letter: "D",
    code: "07",
    title: "Risk & Safeguarding Signals",
    subtitle:
      "The D in R.E.A.D.Y. \u2014 a Yes to any safeguarding item triggers Manual Review Required and places automated scoring on hold. Answer in general terms only \u2014 do not include names, addresses, or identifying details.",
    questions: [
      {
        id: "risk_safeguarding_concern",
        prompt: "Serious safeguarding concern visible at the organisation today?",
        helper: "High-level composite. Yes if any active concern about participant safety, abuse, neglect, medication, restrictive practice, or supervision adequacy exists.",
        type: "single",
        options: YES_NO_OPTIONS
      },
      {
        id: "risk_payment_concern",
        prompt: "Payment or claiming concern visible?",
        helper: "Any irregularity, dispute, or unresolved claim against NDIS funding.",
        type: "single",
        options: YES_NO_OPTIONS
      },
      {
        id: "risk_participant_transfer",
        prompt: "Participant transfer required (planned or in progress)?",
        helper: "Any participant currently needing to be moved to another provider or arrangement.",
        type: "single",
        options: YES_NO_OPTIONS
      },
      { id: "safeguarding_abuse_neglect", prompt: "Do you currently have any concern about possible abuse or neglect of a person you support?", type: "single", options: SAFEGUARDING_OPTIONS },
      { id: "safeguarding_medication_safety", prompt: "Do you currently have any concern about the safety of medication support being provided?", type: "single", options: SAFEGUARDING_OPTIONS },
      { id: "safeguarding_restrictive_practice", prompt: "Do you currently have any concern about the use of restrictive practices in your service?", type: "single", options: SAFEGUARDING_OPTIONS },
      { id: "safeguarding_serious_incident", prompt: "In the last six months, is there any serious incident you suspect was not reported correctly?", type: "single", options: SAFEGUARDING_OPTIONS },
      { id: "safeguarding_staffing_adequacy", prompt: "Are you currently concerned that staffing or supervision is not adequate for participant safety?", type: "single", options: SAFEGUARDING_OPTIONS },
      { id: "safeguarding_exit_continuity", prompt: "Is the organisation considering an unplanned exit or transition that may affect participant continuity?", type: "single", options: SAFEGUARDING_OPTIONS }
    ]
  },
  {
    id: "plan",
    letter: "Y",
    code: "08",
    title: "Your 30 / 60 / 90 Day Plan",
    subtitle:
      "The Y in R.E.A.D.Y. \u2014 the readiness priority you intend to address in the next quarter.",
    questions: [
      {
        id: "plan_priority",
        prompt: "What is your primary readiness priority for the next 90 days?",
        type: "single",
        options: [
          { value: "registration", label: "Achieving or maintaining registration" },
          { value: "audit", label: "Audit preparation or re-audit" },
          { value: "uplift", label: "Operational uplift in current registration" },
          { value: "exit", label: "Planning a wind-down or transition" },
          { value: "unsure", label: "Unsure" }
        ]
      },
      {
        id: "plan_timeframe",
        prompt: "What internal timeframe is the organisation working to?",
        type: "single",
        options: [
          { value: "lt_30", label: "Less than 30 days" },
          { value: "30_60", label: "30 \u2013 60 days" },
          { value: "60_90", label: "60 \u2013 90 days" },
          { value: "gt_90", label: "More than 90 days" },
          { value: "no_fixed", label: "No fixed timeframe" }
        ]
      },
      {
        id: "plan_notes",
        prompt: "Anything else the readiness review should be aware of? (Optional)",
        helper:
          "Plain English only. Do not include participant names, addresses, medical histories, identifying incident details, or worker private information. Asking for legal advice, audit certification, registration approval, or a compliance guarantee will route this case to manual review.",
        type: "text",
        optional: true
      }
    ]
  },
  {
    id: "legal_referral",
    letter: "\u00a7",
    code: "09",
    title: "Legal & Documentation Referral Triggers",
    subtitle:
      "Readiness indicators that decide whether a provider should be referred for independent legal, audit, privacy, workforce or regulatory review. These questions do not produce a legal opinion. Answer in general terms only \u2014 do not include participant names, addresses, or identifying details.",
    questions: [
      {
        id: "ld_registration_status_confirmed",
        prompt: "Has current NDIS registration status and scope been confirmed against the certificate?",
        helper: "Confirmed means the provider has sighted the current registration certificate and matched the scope to the services actually delivered.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_sil_0138_uncertainty",
        prompt: "Is there any uncertainty about the SIL / registration group 0138 exposure of this provider?",
        helper: "Uncertainty about whether SIL is currently being delivered, whether 0138 applies, or which registration group covers the service.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_service_agreements_current",
        prompt: "Are current participant service agreement templates in place?",
        helper: "A current, version-controlled template covering scope, pricing basis, cancellation and change-of-circumstance handling.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_privacy_consent_current",
        prompt: "Are privacy, consent and breach-response materials current?",
        helper: "Privacy policy, consent form and documented breach-response pathway that reflect current handling arrangements.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_complaints_incidents_documented",
        prompt: "Are complaints and incident procedures documented and in use?",
        helper: "Written procedures, an active recording register and an escalation loop that is actually followed.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_current_dispute_or_concern",
        prompt: "Is there any active dispute, unresolved complaint or unresolved incident today?",
        helper: "General terms only. Do not describe individuals or identifying incident detail.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_worker_screening_training_supervision",
        prompt: "Are worker screening, training and supervision arrangements documented and current?",
        helper: "Screening register, training matrix with currency, and documented supervision arrangements.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_contractors_labour_hire",
        prompt: "Does the provider use contractors or a labour-hire arrangement to deliver supports?",
        helper: "Any part of service delivery relies on contractors, sub-contracted workers, or labour hire.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_tenancy_support_overlap",
        prompt: "Is there any overlap between housing / tenancy and support arrangements?",
        helper: "The same provider controls tenancy or housing arrangements while also delivering the support.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_marketing_claims_checked",
        prompt: "Have public website, brochure and proposal claims been checked for accuracy and boundary compliance?",
        helper: "No overstated compliance, audit-readiness, certification or outcome guarantees in public-facing material.",
        type: "single",
        options: YES_NO_UNSURE_NA_OPTIONS
      },
      {
        id: "ld_housing_security_provider_choice",
        prompt: "Do participants have documented housing security and a genuine choice of provider?",
        helper: "Readiness / referral signal only. Not a tenancy-law determination. Downstream evidence review confirms the finding. Leave unanswered if not yet assessed; mark Not applicable where the provider does not control tenancy.",
        type: "single",
        optional: true,
        options: YES_PARTLY_NO_UNSURE_NA_OPTIONS
      }
    ]
  }
];

export const TOTAL_QUESTIONS = SECTIONS.reduce(
  (acc, s) => acc + s.questions.filter((q) => !q.optional).length,
  0
);

export function getOptions(section, question) {
  if (question && question.options) return question.options;
  if (section.sharedOptions) return section.sharedOptions;
  return [];
}


// ---------------------------------------------------------------------------
// Phase 8B — Provider Intake / Audit Readiness Questionnaire separation.
//
// The canonical PCX1 workflow requires that the "Provider Intake" (factual
// organisational state) and the "Audit Readiness Questionnaire" (operational
// readiness self-assessment) are two separate but connected client stages.
//
// SECTIONS above stays intact — it is the single source of truth for section
// definitions AND remains exported so historical saved assessments and any
// combined-flow test path continue to work byte-identically.
//
// PROVIDER_INTAKE_SECTIONS and QUESTIONNAIRE_SECTIONS are derived slices, not
// copies — every reference is === to the same underlying object.
// ---------------------------------------------------------------------------

const PROVIDER_INTAKE_SECTION_IDS = ["profile", "registration", "sil_delivery", "application"];
const QUESTIONNAIRE_SECTION_IDS = ["evidence", "risk", "safeguarding", "plan", "legal_referral"];

export const PROVIDER_INTAKE_SECTIONS = SECTIONS.filter((s) =>
  PROVIDER_INTAKE_SECTION_IDS.includes(s.id)
);

export const QUESTIONNAIRE_SECTIONS = SECTIONS.filter((s) =>
  QUESTIONNAIRE_SECTION_IDS.includes(s.id)
);

// True when every required field across Sections 01-04 has a stored answer.
// Used by /questionnaire to decide whether to allow direct entry or bounce the
// user back to /intake. Also handles the legacy nine-section saved-assessment
// case: an older localStorage blob that already contains complete intake
// answers is treated as "intake complete" and may proceed directly to
// /questionnaire without re-entry.
export function isProviderIntakeComplete(answers) {
  if (!answers || typeof answers !== "object") return false;
  for (const section of PROVIDER_INTAKE_SECTIONS) {
    const required = (section.questions || []).filter((q) => !q.optional);
    for (const q of required) {
      const v = answers[q.id];
      if (v === undefined || v === null || String(v).trim() === "") {
        return false;
      }
    }
  }
  return true;
}
